export interface NeuralOptions {
  width: number;
  height: number;
  time: number;
  distance: number;
  mobile: boolean;
  reduced: boolean;
  skyImage: HTMLImageElement | null;
  vanishing: Point | null;
}

export interface Point { x: number; y: number; }

interface SceneState {
  width: number;
  height: number;
  mobile: boolean;
  hx: number;
  hy: number;
  center: number;
  span: number;
  time: number;
  distance: number;
}

const TAU = Math.PI * 2;

const CIRCUIT_PATHS: number[][][] = [
  [[1230, 375], [1200, 375], [1172, 354], [1119, 359]],
  [[1228, 396], [1180, 396], [1154, 375], [1106, 382]],
  [[1228, 421], [1193, 423], [1150, 444], [1100, 455], [1003, 458]],
  [[1225, 446], [1180, 447], [1145, 470], [1090, 474], [1040, 497]],
  [[1460, 370], [1518, 365], [1548, 336], [1548, 294]],
  [[1464, 400], [1530, 396], [1590, 365], [1593, 319]],
  [[1462, 435], [1520, 434], [1546, 447], [1647, 443]],
  [[1463, 457], [1550, 451], [1584, 470], [1624, 471]],
  [[1240, 341], [1241, 304], [1228, 288], [1229, 272], [1234, 111]],
  [[1260, 332], [1264, 298], [1238, 273], [1239, 235]],
  [[1347, 318], [1348, 274], [1339, 264], [1342, 244], [1343, 193]],
  [[1408, 321], [1407, 274], [1438, 244], [1438, 203], [1471, 120]],
  [[1255, 555], [1252, 577], [1196, 625], [1197, 688]],
  [[1299, 563], [1298, 615], [1297, 639]],
  [[1363, 563], [1363, 602], [1387, 625], [1464, 630]],
  [[1417, 552], [1418, 597], [1468, 627], [1607, 651]],
];

function random(seed: number): () => number {
  let value = seed >>> 0;
  return () => {
    value = (Math.imul(value, 1664525) + 1013904223) >>> 0;
    return value / 4294967296;
  };
}

function createScene(options: NeuralOptions): SceneState {
  const width = Math.max(1, options.width);
  const height = Math.max(1, options.height);
  const mobile = options.mobile;
  return {
    width, height, mobile,
    hx: width * (mobile ? .18 : .43),
    hy: height * (mobile ? .74 : .56),
    center: width * (mobile ? .6 : .73),
    span: width * (mobile ? .98 : .63),
    time: options.reduced ? 0 : options.time,
    distance: options.reduced ? 0 : options.distance,
  };
}

function project(s: SceneState, lane: number, z: number, elevation = 0): Point {
  return {
    x: s.hx + (s.center + lane * s.span - s.hx) / z,
    y: s.hy + (s.height - s.hy - elevation * s.height) / z,
  };
}

function line(ctx: CanvasRenderingContext2D, from: Point, to: Point, color: string, width = 1): void {
  ctx.beginPath();
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(to.x, to.y);
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.stroke();
}

function glow(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number, color: string, bounds: SceneState): void {
  const fill = ctx.createRadialGradient(x, y, 0, x, y, radius);
  fill.addColorStop(0, color);
  fill.addColorStop(1, 'transparent');
  ctx.fillStyle = fill;
  ctx.fillRect(0, 0, bounds.width, bounds.height);
}

function quietLeft(ctx: CanvasRenderingContext2D, s: SceneState): void {
  const shade = ctx.createLinearGradient(0, 0, s.width * .56, 0);
  shade.addColorStop(0, 'rgba(3,5,7,.78)');
  shade.addColorStop(.58, 'rgba(3,5,7,.48)');
  shade.addColorStop(1, 'rgba(3,5,7,0)');
  ctx.fillStyle = shade;
  ctx.fillRect(0, 0, s.width * .56, s.height);
  const vignette = ctx.createLinearGradient(0, 0, 0, s.height);
  vignette.addColorStop(0, 'rgba(0,0,0,.2)');
  vignette.addColorStop(.6, 'transparent');
  vignette.addColorStop(1, 'rgba(0,0,0,.22)');
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, s.width, s.height);
}

export function defaultVanishingPoint(width: number, height: number, mobile: boolean): Point {
  return { x: width * (mobile ? .18 : .20), y: height * (mobile ? .74 : .56) };
}

export function drawNeuralScene(ctx: CanvasRenderingContext2D, options: NeuralOptions): void {
  const s = createScene(options);
  const vanishing = options.vanishing ?? defaultVanishingPoint(s.width, s.height, s.mobile);
  s.hx = vanishing.x;
  s.hy = vanishing.y;
  s.center = s.width * (s.mobile ? .70 : 1.45);
  s.span = s.width * (s.mobile ? .80 : 1.1);
  const travel = s.distance * .55;
  ctx.save();
  ctx.globalAlpha = 1;
  const sky = ctx.createLinearGradient(0, 0, s.width, s.hy);
  sky.addColorStop(0, '#080e12');
  sky.addColorStop(.65, '#10252c');
  sky.addColorStop(1, '#123238');
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, s.width, s.height);

  const skyHeight = s.hy + s.height * .08;
  const image = options.skyImage;
  const imageWidth = s.width * (s.mobile ? 1.8 : 1);
  const imageHeight = imageWidth * 941 / 1672;
  const imageLeft = s.width * (s.mobile ? .88 : .84) - imageWidth * .80;
  const imageTop = s.height * (s.mobile ? .43 : .30) - imageHeight * .48;
  if (image && image.complete && image.naturalWidth) {
    ctx.drawImage(image, imageLeft, imageTop, imageWidth, imageHeight);
  }
  const shade = ctx.createLinearGradient(0, 0, s.width, 0);
  shade.addColorStop(0, 'rgba(3,8,11,.78)');
  shade.addColorStop(.44, 'rgba(3,8,11,.34)');
  shade.addColorStop(.72, 'rgba(3,8,11,.08)');
  shade.addColorStop(1, 'rgba(3,8,11,.02)');
  ctx.fillStyle = shade;
  ctx.fillRect(0, 0, s.width, skyHeight);
  const horizonFade = ctx.createLinearGradient(0, skyHeight * .75, 0, skyHeight);
  horizonFade.addColorStop(0, 'rgba(17,28,31,0)');
  horizonFade.addColorStop(1, '#111c1f');
  ctx.fillStyle = horizonFade;
  ctx.fillRect(0, skyHeight * .75, s.width, skyHeight * .25);

  const clock = s.time * .13 + s.distance * .012;
  for (let index = 0; index < CIRCUIT_PATHS.length; index++) {
    // Path coordinates are in sky-image pixels (1672x941), mapped with the same transform as the image.
    const points: Point[] = CIRCUIT_PATHS[index].map(([x, y]) => ({
      x: imageLeft + x / 1672 * imageWidth,
      y: imageTop + y / 941 * imageHeight,
    }));
    const lengths = points.slice(1).map((point, pointIndex) => Math.hypot(point.x - points[pointIndex].x, point.y - points[pointIndex].y));
    const total = lengths.reduce((sum, length) => sum + length, 0);
    const at = (progress: number): Point => {
      let remaining = Math.max(0, Math.min(1, progress)) * total;
      for (let segment = 0; segment < lengths.length; segment++) {
        if (remaining <= lengths[segment] || segment === lengths.length - 1) {
          const from = points[segment], to = points[segment + 1];
          const t = lengths[segment] ? remaining / lengths[segment] : 0;
          return { x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t };
        }
        remaining -= lengths[segment];
      }
      return points[points.length - 1];
    };
    const color = index % 3 === 0 ? '125,247,201' : '76,222,246';
    ctx.beginPath();
    points.forEach((point, pointIndex) => pointIndex ? ctx.lineTo(point.x, point.y) : ctx.moveTo(point.x, point.y));
    ctx.strokeStyle = `rgba(${color},.12)`;
    ctx.lineWidth = s.mobile ? .7 : 1;
    ctx.stroke();

    const progress = (clock + index * .137) % 1;
    const head = at(progress);
    ctx.save();
    ctx.shadowColor = `rgba(${color},.9)`;
    ctx.shadowBlur = 9;
    for (let tail = 0; tail < 9; tail++) {
      const from = at(progress - (tail + 1) * .013);
      const to = at(progress - tail * .013);
      line(ctx, from, to, `rgba(${color},${.8 * (1 - tail / 9)})`, s.mobile ? 1.3 : 1.8);
    }
    ctx.beginPath();
    ctx.arc(head.x, head.y, s.mobile ? 1.5 : 2.2, 0, TAU);
    ctx.fillStyle = '#d5ffff';
    ctx.fill();
    ctx.restore();

    const end = points[points.length - 1];
    ctx.beginPath();
    ctx.arc(end.x, end.y, s.mobile ? 1.8 : 2.6, 0, TAU);
    ctx.fillStyle = `rgba(${color},${progress > .92 ? .9 : .3})`;
    ctx.fill();
  }

  const groundTop = s.hy - s.height * .075;
  const ground = ctx.createLinearGradient(0, groundTop, 0, s.height);
  ground.addColorStop(0, 'rgba(29,34,35,0)');
  ground.addColorStop(.18, 'rgba(35,41,40,.7)');
  ground.addColorStop(.5, '#292d29');
  ground.addColorStop(1, '#202622');
  ctx.fillStyle = ground;
  ctx.fillRect(0, groundTop, s.width, s.height - groundTop);

  const polygon = (points: Point[], fill: string | CanvasGradient) => {
    ctx.beginPath();
    points.forEach((point, index) => index ? ctx.lineTo(point.x, point.y) : ctx.moveTo(point.x, point.y));
    ctx.closePath();
    ctx.fillStyle = fill;
    ctx.fill();
  };
  const strip = (inner: number, outer: number, near: number, far: number, fill: string | CanvasGradient) => polygon([
    project(s, inner, near), project(s, outer, near),
    project(s, outer, far), project(s, inner, far),
  ], fill);

  for (const side of [-1, 1]) {
    strip(side * 1.17, side * 1.38, 1, 45, '#394039');
    strip(side * 1.38, side * 2.2, 1, 45, '#3b3832');
    strip(side * 1.17, side * 1.2, 1, 45, '#686d61');
  }
  const asphalt = ctx.createLinearGradient(0, s.hy, 0, s.height);
  asphalt.addColorStop(0, '#272c2f');
  asphalt.addColorStop(.3, '#30363a');
  asphalt.addColorStop(1, '#272d32');
  strip(-1, 1, 1, 45, asphalt);

  for (let mark = 0; mark < 8; mark++) {
    ctx.beginPath();
    for (let step = 0; step < 38; step++) {
      const z = 1 + step * .2;
      const lane = -.12 + mark * .055 + Math.sin(z * .7 + mark * .17) * .052;
      const point = project(s, lane, z);
      if (step === 0) ctx.moveTo(point.x, point.y);
      else ctx.lineTo(point.x, point.y);
    }
    ctx.strokeStyle = mark % 3 === 0 ? 'rgba(5,8,10,.17)' : 'rgba(5,8,10,.085)';
    ctx.lineWidth = Math.max(1.4, s.width * (.0018 + mark % 2 * .001));
    ctx.stroke();
  }

  const textureRandom = random(1804);
  for (let fleck = 0; fleck < 85; fleck++) {
    const lane = textureRandom() * 1.92 - .96;
    const z = 1 + ((textureRandom() * 24 - travel) % 24 + 24) % 24;
    const point = project(s, lane, z);
    ctx.fillStyle = fleck % 3 === 0 ? 'rgba(185,194,196,.12)' : 'rgba(4,7,9,.15)';
    const size = Math.max(.5, 2.2 / z);
    ctx.fillRect(point.x, point.y, size * 1.8, size);
  }

  const barrierStep = 1.8;
  const barrierOffset = travel % barrierStep;
  for (const side of [-1, 1]) {
    const lane = side * 1.63;
    for (let panel = 22; panel >= 0; panel--) {
      const near = Math.max(1, 1 + panel * barrierStep - barrierOffset);
      const far = 1 + (panel + 1) * barrierStep - barrierOffset;
      if (far <= 1) continue;
      polygon([
        project(s, lane, near, .045), project(s, lane, near, .105),
        project(s, lane, far, .105), project(s, lane, far, .045),
      ], panel % 3 === 0 ? '#555e63' : '#414a50');
      line(ctx, project(s, lane, near, .11), project(s, lane, far, .11), 'rgba(171,186,192,.48)', 1);
      line(ctx, project(s, lane, near, .04), project(s, lane, near, .12), 'rgba(24,30,34,.75)', Math.max(.7, 2.5 / near));
    }
  }

  const curbStep = .52;
  const curbOffset = travel % curbStep;
  const firstCurb = Math.floor(travel / curbStep);
  for (let segment = 68; segment >= 0; segment--) {
    const near = Math.max(1, 1 + segment * curbStep - curbOffset);
    const far = 1 + (segment + 1) * curbStep - curbOffset;
    if (far <= 1) continue;
    const fill = (firstCurb + segment) % 2 === 0 ? '#dfdfd6' : '#c94237';
    for (const side of [-1, 1]) {
      strip(side, side * 1.18, near, far, fill);
      strip(side * 1.15, side * 1.18, near, far, 'rgba(17,23,26,.2)');
    }
  }
  for (const side of [-1, 1]) {
    line(ctx, project(s, side * .985, 42), project(s, side * .985, 1), '#c9d0cc', 1.2);
    line(ctx, project(s, side * 1.19, 42), project(s, side * 1.19, 1), 'rgba(18,24,25,.6)', 1.5);
  }

  const hazeTop = s.hy - s.height * .055;
  const hazeHeight = s.height * .18;
  const haze = ctx.createLinearGradient(0, hazeTop, 0, hazeTop + hazeHeight);
  haze.addColorStop(0, 'rgba(119,141,151,0)');
  haze.addColorStop(.34, 'rgba(119,141,151,.055)');
  haze.addColorStop(.65, 'rgba(119,141,151,.025)');
  haze.addColorStop(1, 'rgba(119,141,151,0)');
  ctx.fillStyle = haze;
  ctx.fillRect(0, hazeTop, s.width, hazeHeight);
  glow(ctx, s.hx, s.hy, s.width * .29, 'rgba(138,161,170,.085)', s);
  quietLeft(ctx, s);
  ctx.restore();
}
